/**
 * Escolher uma data: digitando, ou num calendário nosso.
 *
 * ── Por que não o `<input type="date">` ──────────────────────────────────
 *
 * O nativo é um em cada navegador, e nenhum é bom para nascimento: no Chrome
 * o calendário abre no mês de hoje e chegar a 1958 é clicar seta seiscentas
 * vezes; no Safari do Mac os três pedaços (dia, mês, ano) se editam um de
 * cada vez com as setas; e a fonte, os cantos e o tema escuro não são os
 * nossos. Quem usa reclamou.
 *
 * ── Digitar primeiro ─────────────────────────────────────────────────────
 *
 * Quem tem a data na mão (o documento do paciente, a ficha impressa) digita:
 * só dígitos, e as barras entram sozinhas. Colar "03/11/1990" ou "1990-11-03"
 * também vale. Enquanto o texto não é uma data inteira, válida e dentro de
 * `min`/`max`, o valor que sai é `''` — nunca meia data. O porquê aparece só
 * ao sair do campo: no meio da digitação, "03/1" não é erro, é pressa.
 *
 * ── O calendário: date picker dialog (WAI-ARIA APG) ──────────────────────
 *
 * O botão ao lado abre um diálogo com o mês em grade (`role="grid"`), mês e
 * ano por select (em nascimento, o ano é o que mais se anda) e as setas. O
 * foco vai para o dia escolhido, ou hoje; o teclado é o do padrão: setas
 * andam dia e semana, PageUp/PageDown mês, com Shift ano, Home/End o começo e
 * o fim da semana, Enter/Espaço escolhem, Esc fecha e devolve o foco ao botão.
 * O Tab circula dentro do diálogo enquanto ele está aberto.
 *
 * ── O diálogo na camada superior, no `body` ──────────────────────────────
 *
 * O mesmo arranjo do `CampoEscolha`, pelo mesmo motivo (ver o cabeçalho de
 * `campo-escolha.tsx`): `popover="manual"` para sair por cima da coluna que
 * rola, e portal no `body` porque o Safari põe na tela, no canto, um popover
 * que mora dentro de ancestral com `transform` animado — a etapa do
 * agendamento entra deslizando. A posição é calculada antes de pintar:
 * embaixo do campo, virando para cima quando não cabe. Fecha no clique fora
 * (lembrando que o diálogo não está dentro do campo no DOM), no Esc, na
 * rolagem da página e no resize.
 *
 * ── Por que não a grade do `CalendarioDeVagas` ───────────────────────────
 *
 * A conta do dia (meses, semana, `AAAA-MM-DD`) é a mesma e mora em
 * `data-local.ts`, para os dois. A grade não: lá cada dia é um botão de
 * alternar (`aria-pressed`) num grupo, cada um com a sua parada de Tab, e o dia
 * sem vaga fica travado; aqui é uma grade do APG, com linhas de semana, uma
 * parada só (o dia da vez) e as setas andando entre os dias. Juntar as duas
 * mudaria o teclado e o leitor de tela das vagas, que já estão em uso.
 *
 * ── Datas sem fuso ───────────────────────────────────────────────────────
 *
 * Toda a conta é com o dia local `AAAA-MM-DD` (`data-local.ts`): "hoje" à
 * noite continua sendo hoje.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Icone } from '@/shared/ui/icones';
import { CampoSelecao, Envelope, descritores, type TamanhoCampo } from './campo';
import {
  INICIAIS_DA_SEMANA,
  MESES,
  SEMANA,
  anosDoIntervalo,
  cursorNoMascarado,
  dataPorExtenso,
  destinoDaTecla,
  diaDoTexto,
  hojeLocal,
  intervaloDoCalendario,
  lerIso,
  lerTexto,
  limitar,
  mascarar,
  mensagemDaLeitura,
  mesForaDoIntervalo,
  noMes,
  partes,
  semanasDoMes,
  somarMeses,
  textoColado,
  textoDoDia,
  type DiaIso,
} from './data-local';
import './campo-data.css';

export type CampoDataProps = {
  rotulo: string;
  ajuda?: string;
  /** Erro de quem monta a tela ("Informe o nascimento."). Ganha do erro de formato. */
  erro?: string;
  /** `AAAA-MM-DD`, ou `''` sem data. */
  valor: DiaIso | '';
  /** Só sai data inteira, válida e dentro dos limites; o resto sai `''`. */
  aoMudar: (valor: DiaIso | '') => void;
  /** `AAAA-MM-DD`. Sem ele, o calendário volta 120 anos. */
  min?: DiaIso;
  /** `AAAA-MM-DD`. Para nascimento, `hojeLocal()`. */
  max?: DiaIso;
  desabilitado?: boolean;
  id?: string;
  tamanho?: TamanhoCampo;
  /** Hoje, `AAAA-MM-DD`, para a marca e as mensagens. Padrão: o relógio local. */
  hoje?: DiaIso;
};

const MARGEM = 4;
const BEIRADA = 8;

/** O que recebe Tab dentro do diálogo: os selects, as setas e o dia da vez. */
const FOCAVEIS = 'button:not(:disabled), select:not(:disabled), [tabindex="0"]';

export function CampoData({
  rotulo,
  ajuda,
  erro,
  valor,
  aoMudar,
  min,
  max,
  desabilitado = false,
  id: idRecebido,
  tamanho = 'padrao',
  hoje: hojeRecebido,
}: CampoDataProps): ReactNode {
  const gerado = useId();
  const id = idRecebido ?? gerado;
  const idDoDialogo = `${id}-calendario`;
  const idDoMes = `${id}-mes-visivel`;

  const hoje = hojeRecebido ?? hojeLocal();
  const { piso, teto } = intervaloDoCalendario(hoje, min, max);

  const raiz = useRef<HTMLSpanElement>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const dialogo = useRef<HTMLDivElement | null>(null);
  // O cursor que o texto mascarado deve receber, depois de pintar.
  const cursorPendente = useRef<number | null>(null);
  // Só a navegação pela grade leva o foco ao dia; o select e as setas não.
  const levarFoco = useRef(false);

  const [texto, definirTexto] = useState(() => textoDoDia(valor));
  const [erroDeFormato, definirErroDeFormato] = useState<string | null>(null);
  const [aberto, definirAberto] = useState(false);
  const [focado, definirFocado] = useState<DiaIso>(hoje);
  const [posicao, definirPosicao] = useState<{ top: number; left: number } | null>(null);

  /*
   * O valor mudou por fora (o formulário limpou, o cadastro carregou): o texto
   * acompanha, no próprio render. Se o valor novo é o que o texto já diz — foi
   * este campo que o mandou —, o texto fica como a pessoa digitou.
   */
  const [valorVisto, definirValorVisto] = useState(valor);
  if (valor !== valorVisto) {
    definirValorVisto(valor);
    if (diaDoTexto(texto, min, max) !== valor) {
      definirTexto(textoDoDia(valor));
      definirErroDeFormato(null);
    }
  }

  function mudarTexto(novo: string): void {
    definirTexto(novo);
    definirErroDeFormato(null);
    const dia = diaDoTexto(novo, min, max);
    if (dia !== valor) aoMudar(dia);
  }

  function aoDigitar(evento: ChangeEvent<HTMLInputElement>): void {
    const bruto = evento.currentTarget.value;
    const colado = textoColado(bruto);
    const novo = colado ?? mascarar(bruto);
    const cursor = evento.currentTarget.selectionStart ?? bruto.length;
    const digitosAntes = bruto.slice(0, cursor).replace(/\D/g, '').length;
    cursorPendente.current = colado ? novo.length : cursorNoMascarado(novo, digitosAntes);
    mudarTexto(novo);
  }

  function aoColar(evento: ClipboardEvent<HTMLInputElement>): void {
    // A data inteira colada troca o que havia; o resto passa pela máscara.
    const colado = textoColado(evento.clipboardData.getData('text'));
    if (!colado) return;
    evento.preventDefault();
    cursorPendente.current = colado.length;
    mudarTexto(colado);
  }

  function aoSair(evento: FocusEvent<HTMLInputElement>): void {
    // Indo para o botão do calendário ou para dentro dele, ainda não acabou.
    const para = evento.relatedTarget as Node | null;
    if (para && (raiz.current?.contains(para) || dialogo.current?.contains(para))) return;
    definirErroDeFormato(mensagemDaLeitura(lerTexto(texto, min, max), hoje));
  }

  useLayoutEffect(() => {
    const cursor = cursorPendente.current;
    cursorPendente.current = null;
    const campo = entrada.current;
    if (cursor === null || !campo || document.activeElement !== campo) return;
    campo.setSelectionRange(cursor, cursor);
  }, [texto]);

  // ── O calendário ───────────────────────────────────────────────────────

  function abrir(): void {
    if (desabilitado) return;
    const escolhido = lerIso(valor);
    definirFocado(limitar(escolhido ?? hoje, piso, teto));
    levarFoco.current = true;
    definirAberto(true);
  }

  function fechar(devolverFoco: boolean): void {
    definirAberto(false);
    if (devolverFoco) botao.current?.focus();
  }

  function escolher(dia: DiaIso): void {
    if (dia < piso || dia > teto) return;
    cursorPendente.current = null;
    mudarTexto(textoDoDia(dia));
    fechar(true);
  }

  function irPara(dia: DiaIso, comFoco: boolean): void {
    levarFoco.current = comFoco;
    definirFocado(limitar(dia, piso, teto));
  }

  const visivel = partes(focado);
  const semanas = semanasDoMes(visivel.ano, visivel.mes);
  const temAntes = focado.slice(0, 7) > piso.slice(0, 7);
  const temDepois = focado.slice(0, 7) < teto.slice(0, 7);
  const escolhido = lerIso(valor);

  // Posição antes de pintar: entre pintar no canto e mover, o calendário piscaria torto.
  useLayoutEffect(() => {
    const caixa = dialogo.current;
    const ancora = raiz.current?.getBoundingClientRect();
    if (!aberto || !caixa || !ancora) return;

    if (typeof caixa.showPopover === 'function' && !caixa.matches(':popover-open')) {
      try {
        caixa.showPopover();
      } catch {
        // Já aberto por outro caminho: a medição abaixo continua valendo.
      }
    }

    const { height: altura, width: largura } = caixa.getBoundingClientRect();
    const cabeEmbaixo = ancora.bottom + MARGEM + altura <= window.innerHeight - BEIRADA;
    // Alinhado à esquerda do campo; se passar da tela, à direita dele.
    const aEsquerda = ancora.left + largura <= window.innerWidth - BEIRADA;
    const left = aEsquerda ? ancora.left : ancora.right - largura;
    definirPosicao({
      top: cabeEmbaixo ? ancora.bottom + MARGEM : Math.max(BEIRADA, ancora.top - MARGEM - altura),
      left: Math.max(BEIRADA, Math.min(left, window.innerWidth - BEIRADA - largura)),
    });
    // Mês de cinco ou de seis semanas muda a altura: virado para cima, ele precisa encostar no campo.
  }, [aberto, semanas.length]);

  useEffect(() => {
    if (!aberto) definirPosicao(null);
  }, [aberto]);

  // Com o diálogo no lugar (escondido, o foco não pega), o dia da vez recebe o foco.
  const posicionado = posicao !== null;
  useEffect(() => {
    if (!aberto || !posicionado || !levarFoco.current) return;
    levarFoco.current = false;
    dialogo.current
      ?.querySelector<HTMLButtonElement>(`[data-dia="${focado}"]`)
      ?.focus({ preventScroll: true });
  }, [aberto, posicionado, focado]);

  /*
   * Fecha no clique fora, e quando a página rola ou muda de tamanho: o
   * calendário pertence ao campo, e um campo que sai do lugar o deixaria
   * flutuando sobre outra linha. O diálogo mora no `body` (portal): o clique
   * nele não é "fora".
   */
  useEffect(() => {
    if (!aberto) return;
    const foraDele = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (!raiz.current?.contains(alvo) && !dialogo.current?.contains(alvo)) fechar(false);
    };
    const rolou = (evento: Event) => {
      if (!dialogo.current?.contains(evento.target as Node)) fechar(false);
    };
    const mudouTamanho = () => fechar(false);
    document.addEventListener('mousedown', foraDele);
    window.addEventListener('scroll', rolou, true);
    window.addEventListener('resize', mudouTamanho);
    return () => {
      document.removeEventListener('mousedown', foraDele);
      window.removeEventListener('scroll', rolou, true);
      window.removeEventListener('resize', mudouTamanho);
    };
  }, [aberto]);

  function noTecladoDoDialogo(evento: KeyboardEvent<HTMLDivElement>): void {
    if (evento.key === 'Escape') {
      evento.preventDefault();
      evento.stopPropagation();
      fechar(true);
      return;
    }
    if (evento.key !== 'Tab' || !dialogo.current) return;
    // Os outros dias são botões com `tabIndex` -1: estão no seletor, mas não no Tab.
    const focaveis = [...dialogo.current.querySelectorAll<HTMLElement>(FOCAVEIS)].filter(
      (elemento) => elemento.tabIndex >= 0,
    );
    const primeiro = focaveis[0];
    const ultimo = focaveis[focaveis.length - 1];
    if (!primeiro || !ultimo) return;
    if (evento.shiftKey && document.activeElement === primeiro) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!evento.shiftKey && document.activeElement === ultimo) {
      evento.preventDefault();
      primeiro.focus();
    }
  }

  function noTecladoDaGrade(evento: KeyboardEvent<HTMLTableSectionElement>): void {
    const destino = destinoDaTecla(evento.key, evento.shiftKey, focado);
    if (destino) {
      evento.preventDefault();
      irPara(destino, true);
      return;
    }
    // O `preventDefault` também segura o clique que o botão daria: escolheria duas vezes.
    if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      escolher(focado);
    }
  }

  const estiloDoDialogo = posicao
    ? { top: `${posicao.top}px`, left: `${posicao.left}px` }
    : { top: 0, left: 0, visibility: 'hidden' as const };

  const classes = [
    'campo__controle',
    'campo-data__entrada',
    tamanho === 'alto' ? 'campo__controle--alto' : null,
    tamanho === 'miudo' ? 'campo__controle--miudo' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const erroMostrado = erro ?? erroDeFormato ?? undefined;
  const anos = anosDoIntervalo(piso, teto);

  return (
    <Envelope id={id} rotulo={rotulo} ajuda={ajuda} erro={erroMostrado}>
      <span
        className="campo-data"
        ref={raiz}
        data-aberto={aberto ? 'sim' : undefined}
        data-tamanho={tamanho}
      >
        <input
          ref={entrada}
          id={id}
          type="text"
          className={classes}
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder="dd/mm/aaaa"
          maxLength={10}
          aria-invalid={erroMostrado ? true : undefined}
          aria-describedby={descritores(id, ajuda, erroMostrado)}
          disabled={desabilitado}
          value={texto}
          onChange={aoDigitar}
          onPaste={aoColar}
          onBlur={aoSair}
        />
        <button
          ref={botao}
          type="button"
          className="campo-data__abrir"
          aria-label="Abrir calendário"
          aria-haspopup="dialog"
          aria-expanded={aberto}
          aria-controls={aberto ? idDoDialogo : undefined}
          disabled={desabilitado}
          onClick={() => (aberto ? fechar(false) : abrir())}
        >
          <Icone nome="calendario" />
        </button>
        {aberto
          ? createPortal(
              <div
                ref={(elemento) => {
                  dialogo.current = elemento;
                }}
                id={idDoDialogo}
                role="dialog"
                aria-label="Escolher data"
                className="campo-data__calendario"
                popover="manual"
                style={estiloDoDialogo}
                onKeyDown={noTecladoDoDialogo}
              >
                <div className="campo-data__cab">
                  <button
                    type="button"
                    className="campo-data__seta"
                    aria-label="Mês anterior"
                    disabled={!temAntes}
                    onClick={() => irPara(somarMeses(focado, -1), false)}
                  >
                    <Icone nome="voltar" />
                  </button>
                  <CampoSelecao
                    rotuloOculto="Mês"
                    tamanho="miudo"
                    value={visivel.mes}
                    onChange={(evento) =>
                      irPara(noMes(focado, visivel.ano, Number(evento.currentTarget.value)), false)
                    }
                  >
                    {MESES.map((nome, mes) => (
                      <option
                        key={nome}
                        value={mes}
                        disabled={mesForaDoIntervalo(visivel.ano, mes, piso, teto)}
                      >
                        {nome}
                      </option>
                    ))}
                  </CampoSelecao>
                  <CampoSelecao
                    rotuloOculto="Ano"
                    tamanho="miudo"
                    value={visivel.ano}
                    onChange={(evento) =>
                      irPara(noMes(focado, Number(evento.currentTarget.value), visivel.mes), false)
                    }
                  >
                    {anos.map((ano) => (
                      <option key={ano} value={ano}>
                        {ano}
                      </option>
                    ))}
                  </CampoSelecao>
                  <button
                    type="button"
                    className="campo-data__seta"
                    aria-label="Próximo mês"
                    disabled={!temDepois}
                    onClick={() => irPara(somarMeses(focado, 1), false)}
                  >
                    <Icone nome="direita" />
                  </button>
                </div>
                <p className="visualmente-oculto" id={idDoMes} aria-live="polite">
                  {MESES[visivel.mes]} de {visivel.ano}
                </p>
                <table className="campo-data__grade" role="grid" aria-labelledby={idDoMes}>
                  <thead>
                    <tr>
                      {INICIAIS_DA_SEMANA.map((inicial, i) => (
                        <th key={i} scope="col" className="campo-data__semana">
                          <span aria-hidden="true">{inicial}</span>
                          <span className="visualmente-oculto">{SEMANA[i]}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody onKeyDown={noTecladoDaGrade}>
                    {semanas.map((semana, linha) => (
                      <tr key={linha}>
                        {semana.map((dia, coluna) =>
                          dia === null ? (
                            <td key={`vazia-${coluna}`} />
                          ) : (
                            <td key={dia} aria-selected={dia === escolhido}>
                              <button
                                type="button"
                                className="campo-data__dia"
                                data-dia={dia}
                                tabIndex={dia === focado ? 0 : -1}
                                aria-label={dataPorExtenso(dia)}
                                aria-current={dia === hoje ? 'date' : undefined}
                                disabled={dia < piso || dia > teto}
                                onClick={() => escolher(dia)}
                              >
                                {partes(dia).dia}
                              </button>
                            </td>
                          ),
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>,
              document.body,
            )
          : null}
      </span>
    </Envelope>
  );
}
